import { useState, useEffect } from "react";
import {
  Label,
  Button,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pushpress/pushpress-ui";
import { CheckIcon, XIcon, Pencil } from "lucide-react";
import { GENDER_OPTIONS } from "../../constants";

type EditableFieldProps = {
  label: string;
  value?: string | null;
  onSave: (value: string) => void;
  hasData?: boolean;
};

/**
 * In DB:
 * "M" = Male
 * "F" = Female
 * "" = Not specified
 */
export default function EditableGenderSelectField({
  label,
  value = "",
  onSave,
  hasData = false,
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    if (editValue === "OTHER") {
      onSave("");
      setIsEditing(false);
      return;
    }
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  useEffect(() => {
    // Need "OTHER" as we can't have an empty string as value in an option
    setEditValue(value || GENDER_OPTIONS[2].value);
  }, [value]);

  if (!hasData) {
    return (
      <div className="tw-group">
        <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
        <Skeleton className="tw-h-4 tw-w-1/2" />
      </div>
    );
  }

  return (
    <div className="tw-group tw-h-12">
      <Label className="tw-text-xs tw-text-neutral-600">{label}</Label>
      {isEditing ? (
        <div className="tw-flex tw-items-center">
          <Select
            value={editValue}
            onValueChange={(value) => setEditValue(value)}
          >
            <SelectTrigger className="tw-h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GENDER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7"
            onClick={handleSave}
          >
            <CheckIcon className="tw-h-4 tw-w-4 tw-text-success" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="tw-h-7 tw-w-7"
            onClick={handleCancel}
          >
            <XIcon className="tw-h-4 tw-w-4 tw-text-error" />
          </Button>
        </div>
      ) : (
        <div
          className="tw-cursor-pointer tw-py-1 hover:tw-bg-neutral-100 tw-transition-colors tw-group tw-flex tw-items-center"
          onClick={() => setIsEditing(true)}
          onKeyDown={() => setIsEditing(true)}
          role="button"
          tabIndex={0}
        >
          <p className="tw-font-medium group-hover:tw-text-primary tw-transition-colors">
            {GENDER_OPTIONS.find((option) => option.value === value)?.label ||
              GENDER_OPTIONS[2].label}
          </p>
          <div className="tw-ml-2 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
            <Pencil className="tw-h-4 tw-w-4 tw-text-neutral-600" />
          </div>
        </div>
      )}
    </div>
  );
}
